import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  PiHeadphones,
  PiPauseFill,
  PiPlayFill,
  PiWarningCircle,
  PiX,
} from "react-icons/pi";
import { Paragraph, Story } from "./story";
import { fetchSentenceAudioUrl, refusalMessage } from "./queries";
import { lstr } from "./localization";

// readAlongSentenceIndices lists the sentences of the learned-language story
// in reading order: chapter by chapter, paragraph by paragraph.
export function readAlongSentenceIndices(story: Story): number[] {
  return story.chapters.flatMap((chapter) =>
    chapter.paragraphs.flatMap((paragraph) =>
      paragraph.sentences
        .filter((sentence) => sentence.hasAudio)
        .map((sentence) => sentence.index),
    ),
  );
}

// readAlongStartPosition is where in sentenceIndices reading starts: at the
// first readable sentence that is not yet scrolled away (given top to
// bottom), or at the beginning when all of them are (the reader is at the
// end of the page).
export function readAlongStartPosition(
  sentenceIndices: number[],
  sentenceIndicesNotScrolledAway: number[],
): number {
  for (const sentenceIndex of sentenceIndicesNotScrolledAway) {
    const position = sentenceIndices.indexOf(sentenceIndex);
    if (position >= 0) return position;
  }
  return 0;
}

// readAlongSentenceIndexIn passes the sentence being read on only to the
// part of the story (a chapter's paragraphs, or one paragraph) that holds it,
// so the other, memoized parts don't re-render as the reading moves on.
export function readAlongSentenceIndexIn(
  paragraphs: Paragraph[],
  sentenceIndex: number | null,
): number | null {
  const holdsSentence = paragraphs.some((paragraph) =>
    paragraph.sentences.some((sentence) => sentence.index === sentenceIndex),
  );
  return holdsSentence ? sentenceIndex : null;
}

// readAlongSentenceProps marks the element of a learned-language sentence,
// so read-along finds it (to start at the sentence in view and to follow the
// one being read), and highlights it while it is read: a fill plus a ring,
// which still shows without color vision.
export function readAlongSentenceProps(
  sentenceIndex: number,
  beingRead: boolean,
) {
  return {
    "data-read-along-sentence": sentenceIndex,
    className: `rounded-[3px] box-decoration-clone transition-[background-color,box-shadow] duration-300 ${
      beingRead ? "bg-highlight-light ring-2 ring-highlight" : ""
    }`,
  };
}

// The reader toolbar's height (h-14 in TopMenu): text above it is covered.
const TOOLBAR_HEIGHT_PX = 56;

function sentenceElements(): HTMLElement[] {
  return Array.from(
    document.querySelectorAll<HTMLElement>("[data-read-along-sentence]"),
  );
}

// The sentences not yet scrolled away under the toolbar, top to bottom.
function sentenceIndicesNotScrolledAway(): number[] {
  return sentenceElements()
    .filter(
      (element) => element.getBoundingClientRect().bottom > TOOLBAR_HEIGHT_PX,
    )
    .map((element) => Number(element.dataset.readAlongSentence));
}

// scrollSentenceIntoView follows the sentence being read, leaving the page
// alone while the sentence is comfortably in view.
function scrollSentenceIntoView(sentenceIndex: number) {
  const element = sentenceElements().find(
    (element) => Number(element.dataset.readAlongSentence) === sentenceIndex,
  );
  if (!element) return;
  const rect = element.getBoundingClientRect();
  if (rect.top >= TOOLBAR_HEIGHT_PX && rect.bottom <= window.innerHeight * 0.8)
    return;
  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  element.scrollIntoView({
    block: "center",
    behavior: reducedMotion ? "auto" : "smooth",
  });
}

export type ReadAlongStatus =
  | "stopped"
  | "loading"
  | "playing"
  | "paused"
  | "error";

interface PlaybackState {
  status: ReadAlongStatus;
  // The sentence being read (its index in the learned-language
  // localization); null while stopped.
  sentenceIndex: number | null;
  // Why reading failed, when status is "error" and the cause is known.
  error?: unknown;
}

const STOPPED: PlaybackState = { status: "stopped", sentenceIndex: null };

// The audio of one sentence; url is set once the request has succeeded.
interface SentenceAudio {
  request: Promise<string>;
  url: string | null;
}

// ReadAlongPlayer reads sentences aloud one after another. It plays them all
// through a single audio element: iOS only lets an element that started
// playing on a tap go on to play more without another tap. It holds the
// audio of the sentence being read and of the next one, fetched ahead so
// the reading flows on.
export class ReadAlongPlayer {
  private readonly audio = new Audio();
  private readonly abortController = new AbortController();
  private readonly audioBySentenceIndex = new Map<number, SentenceAudio>();
  private state: PlaybackState = STOPPED;
  // Position in sentenceIndices of the sentence being read.
  private position = 0;
  // The sentence whose audio the element holds.
  private loadedSentenceIndex: number | null = null;
  // Bumped by every play, pause and stop, so audio that arrives after the
  // reader has moved on is not played.
  private playRequestId = 0;

  constructor(
    private readonly storyId: string,
    private readonly r: string,
    private readonly sentenceIndices: number[],
    private readonly onStateChange: (state: PlaybackState) => void,
  ) {
    this.audio.preservesPitch = true;
    this.audio.onended = () => this.playFrom(this.position + 1);
    this.audio.onerror = () => {
      if (this.state.status === "stopped") return;
      console.error("Read-along audio failed", this.audio.error);
      // The audio itself is broken: a retry fetches it again.
      if (this.loadedSentenceIndex !== null) {
        this.releaseAudio(this.loadedSentenceIndex);
        this.loadedSentenceIndex = null;
      }
      this.setState({
        status: "error",
        sentenceIndex: this.state.sentenceIndex,
      });
    };
    // Constructed on a tap: loading now unlocks the element on iOS for the
    // playback that starts once the first sentence's audio has arrived.
    this.audio.load();
  }

  toggle(startPosition: () => number) {
    switch (this.state.status) {
      case "stopped":
        this.playFrom(startPosition());
        break;
      case "loading":
      case "playing":
        this.pause();
        break;
      case "paused":
        this.resume();
        break;
      case "error":
        this.playFrom(this.position);
        break;
    }
  }

  // Pauses a reading in progress; a paused, stopped or failed one stays as is.
  pauseIfReading() {
    if (this.state.status === "loading" || this.state.status === "playing") {
      this.pause();
    }
  }

  setSpeed(speed: number) {
    // The element resets playbackRate to defaultPlaybackRate on every new
    // sentence.
    this.audio.defaultPlaybackRate = speed;
    this.audio.playbackRate = speed;
  }

  stop() {
    this.playRequestId++;
    this.audio.pause();
    this.position = 0;
    for (const sentenceIndex of Array.from(this.audioBySentenceIndex.keys())) {
      this.releaseAudio(sentenceIndex);
    }
    this.setState(STOPPED);
  }

  dispose() {
    this.audio.onended = null;
    this.audio.onerror = null;
    this.stop();
    this.abortController.abort();
    this.audio.removeAttribute("src");
    this.audio.load();
  }

  private setState(state: PlaybackState) {
    if (
      state.status === this.state.status &&
      state.sentenceIndex === this.state.sentenceIndex
    )
      return;
    this.state = state;
    this.onStateChange(state);
  }

  private pause() {
    this.playRequestId++;
    this.audio.pause();
    this.setState({
      status: "paused",
      sentenceIndex: this.state.sentenceIndex,
    });
  }

  private resume() {
    const sentenceIndex = this.sentenceIndices[this.position];
    if (this.loadedSentenceIndex !== sentenceIndex || this.audio.ended) {
      this.playFrom(this.position);
      return;
    }
    const playRequestId = ++this.playRequestId;
    this.setState({ status: "playing", sentenceIndex });
    this.audio.play().catch((err) => this.fail(playRequestId, err));
  }

  private async playFrom(position: number) {
    if (position >= this.sentenceIndices.length) {
      this.stop();
      return;
    }
    const playRequestId = ++this.playRequestId;
    this.position = position;
    const sentenceIndex = this.sentenceIndices[position];
    this.audioBySentenceIndex.forEach((_, cachedSentenceIndex) => {
      if (
        cachedSentenceIndex !== sentenceIndex &&
        cachedSentenceIndex !== this.sentenceIndices[position + 1]
      ) {
        this.releaseAudio(cachedSentenceIndex);
      }
    });
    const sentenceAudio = this.sentenceAudio(sentenceIndex);
    // Prefetched audio plays right away; "loading" is only for a wait.
    this.setState({
      status: sentenceAudio.url ? "playing" : "loading",
      sentenceIndex,
    });
    try {
      const url = await sentenceAudio.request;
      if (playRequestId !== this.playRequestId) return;
      this.audio.src = url;
      this.loadedSentenceIndex = sentenceIndex;
      await this.audio.play();
      if (playRequestId !== this.playRequestId) return;
      this.setState({ status: "playing", sentenceIndex });
      const nextSentenceIndex = this.sentenceIndices[position + 1];
      if (nextSentenceIndex !== undefined)
        this.sentenceAudio(nextSentenceIndex);
    } catch (err) {
      this.fail(playRequestId, err);
    }
  }

  private fail(playRequestId: number, err: unknown) {
    // A pause or stop interrupts a pending play(); that is not a failure.
    if (playRequestId !== this.playRequestId) return;
    console.error("Failed to read the sentence aloud", err);
    this.setState({
      status: "error",
      sentenceIndex: this.state.sentenceIndex,
      error: err,
    });
  }

  private sentenceAudio(sentenceIndex: number): SentenceAudio {
    const cached = this.audioBySentenceIndex.get(sentenceIndex);
    if (cached) return cached;
    const sentenceAudio: SentenceAudio = {
      request: fetchSentenceAudioUrl(
        this.storyId,
        this.r,
        sentenceIndex,
        "read-along",
        this.abortController.signal,
      ),
      url: null,
    };
    sentenceAudio.request.then(
      (url) => {
        sentenceAudio.url = url;
      },
      () => {
        // A failed request is retried on the next play.
        if (this.audioBySentenceIndex.get(sentenceIndex) === sentenceAudio) {
          this.audioBySentenceIndex.delete(sentenceIndex);
        }
      },
    );
    this.audioBySentenceIndex.set(sentenceIndex, sentenceAudio);
    return sentenceAudio;
  }

  private releaseAudio(sentenceIndex: number) {
    const sentenceAudio = this.audioBySentenceIndex.get(sentenceIndex);
    if (!sentenceAudio) return;
    this.audioBySentenceIndex.delete(sentenceIndex);
    sentenceAudio.request.then(URL.revokeObjectURL, () => {});
  }
}

// The playback speeds: natural, and a little slower for following along.
export const READ_ALONG_SPEEDS = [1, 0.8];

// formatReadAlongSpeed labels a speed the way the reader writes numbers,
// e.g. "0,8×" in German.
export function formatReadAlongSpeed(speed: number, l: string): string {
  return `${speed.toLocaleString(l)}×`;
}

// The chosen speed is remembered on this device.
const READ_ALONG_SPEED_KEY = "readAlongSpeed";

function loadReadAlongSpeed(): number {
  try {
    const speed = Number(localStorage.getItem(READ_ALONG_SPEED_KEY));
    return READ_ALONG_SPEEDS.includes(speed) ? speed : READ_ALONG_SPEEDS[0];
  } catch {
    return READ_ALONG_SPEEDS[0];
  }
}

function storeReadAlongSpeed(speed: number) {
  try {
    localStorage.setItem(READ_ALONG_SPEED_KEY, String(speed));
  } catch (err) {
    console.error("Failed to store the read-along speed", err);
  }
}

export interface ReadAlong {
  // False until the story has sentences to read.
  available: boolean;
  status: ReadAlongStatus;
  sentenceIndex: number | null;
  // Why reading failed, when status is "error" and the cause is known.
  error?: unknown;
  speed: number;
  togglePlayback: () => void;
  // Pauses a reading in progress, e.g. while the reader looks up a word.
  pause: () => void;
  setSpeed: (speed: number) => void;
  stop: () => void;
}

// useReadAlong reads the learned-language story aloud, sentence by sentence,
// starting at the sentence in view, and keeps that sentence in view. A
// different story or language pair stops the reading, and leaving the story
// releases the audio.
export function useReadAlong(
  storyId: string,
  r: string,
  rStory: Story | undefined,
): ReadAlong {
  const sentenceIndices = useMemo(
    () => (rStory ? readAlongSentenceIndices(rStory) : []),
    [rStory],
  );
  const [playback, setPlayback] = useState<PlaybackState>(STOPPED);
  const [speed, setSpeedState] = useState(loadReadAlongSpeed);
  const playerRef = useRef<ReadAlongPlayer | null>(null);
  // A refetch of the same story builds new objects; only different sentences
  // stop the reading.
  const sentenceIndicesKey = sentenceIndices.join(",");

  useEffect(
    () => () => {
      playerRef.current?.dispose();
      playerRef.current = null;
      setPlayback(STOPPED);
    },
    [storyId, r, sentenceIndicesKey],
  );

  useEffect(() => {
    playerRef.current?.setSpeed(speed);
  }, [speed]);

  // Follows each new sentence, and the paused one on resuming (the reader
  // may have scrolled away meanwhile).
  const reading =
    playback.status === "loading" || playback.status === "playing";
  useEffect(() => {
    if (reading && playback.sentenceIndex !== null) {
      scrollSentenceIntoView(playback.sentenceIndex);
    }
  }, [reading, playback.sentenceIndex]);

  const togglePlayback = useCallback(() => {
    if (sentenceIndices.length === 0) return;
    if (!playerRef.current) {
      playerRef.current = new ReadAlongPlayer(
        storyId,
        r,
        sentenceIndices,
        setPlayback,
      );
      playerRef.current.setSpeed(speed);
    }
    playerRef.current.toggle(() =>
      readAlongStartPosition(sentenceIndices, sentenceIndicesNotScrolledAway()),
    );
  }, [storyId, r, sentenceIndices, speed]);

  const setSpeed = useCallback((nextSpeed: number) => {
    setSpeedState(nextSpeed);
    storeReadAlongSpeed(nextSpeed);
  }, []);

  const pause = useCallback(() => playerRef.current?.pauseIfReading(), []);
  const stop = useCallback(() => playerRef.current?.stop(), []);

  return {
    available: sentenceIndices.length > 0,
    status: playback.status,
    sentenceIndex: playback.sentenceIndex,
    error: playback.error,
    speed,
    togglePlayback,
    pause,
    setSpeed,
    stop,
  };
}

// ReadAlongControls is the Listen button of the reader toolbar; the speed is
// chosen in the reading options next to it.
export function ReadAlongControls({
  readAlong,
  l,
}: {
  readAlong: ReadAlong;
  l: string;
}) {
  const strings = lstr(l);
  const { status } = readAlong;
  const label = {
    stopped: strings.read_along_listen,
    loading: strings.listen_loading,
    playing: strings.read_along_pause,
    paused: strings.read_along_resume,
    error: strings.listen_error,
  }[status];

  return (
    <div className="flex shrink-0 items-center">
      <button
        type="button"
        onClick={readAlong.togglePlayback}
        disabled={!readAlong.available}
        aria-label={label}
        title={label}
        className={`btn-ghost h-10 w-10 rounded-full ${
          status === "stopped" ? "" : "bg-cream-dark text-main-text"
        }`}
      >
        {status === "playing" ? (
          <PiPauseFill size={18} />
        ) : status === "paused" ? (
          <PiPlayFill size={18} />
        ) : status === "error" ? (
          <PiWarningCircle size={20} className="text-error" />
        ) : (
          <PiHeadphones
            size={20}
            className={status === "loading" ? "animate-pulse" : ""}
          />
        )}
      </button>
      {/* Hangs below the toolbar at the screen's right edge (the sticky
          header is its containing block), so it stays on screen on phones,
          where the Listen button sits mid-toolbar. */}
      {status === "error" && (
        <div
          role="alert"
          className="animate-pop-in absolute right-2 top-full z-50 sm:right-4 mt-2 flex w-max max-w-[min(280px,calc(100vw-1rem))] items-center gap-2 rounded-2xl border border-border bg-surface py-2 pl-4 pr-2 text-sm text-error shadow-pop"
        >
          {refusalMessage(readAlong.error, strings) ?? strings.listen_error}
          <button
            type="button"
            onClick={readAlong.stop}
            aria-label={strings.close_button_caption}
            title={strings.close_button_caption}
            className="btn-ghost h-8 w-8 shrink-0 rounded-full"
          >
            <PiX size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
